/**
 * Scenario Builder - Create and run what-if scenarios
 */
import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  TextField,
  Button,
  Grid,
  MenuItem,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import {
  PlayArrow as RunIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';
import type {
  SensitivityAnalysisResponse,
  DemandScenarioResponse,
  DisruptionScenarioResponse,
  BudgetOptimizationResponse,
  ServiceLevelTradeoffResponse,
} from '../../types/scenarios';
import TabPanel from '../Common/TabPanel';

const ScenarioBuilder: React.FC = () => {
  const { fmtCurrency, fmtCurrencyLabel } = useCurrencyFormat();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sensitivity Analysis State
  const [sensitivityParams, setSensitivityParams] = useState({
    parameter: 'reorder_point',
    base_value: 100,
    variation_range: 0.5,
    steps: 11,
  });
  const [sensitivityResult, setSensitivityResult] = useState<SensitivityAnalysisResponse | null>(null);

  // Demand Scenario State
  const [demandParams, setDemandParams] = useState({
    scenario_name: 'Demand Spike',
    demand_multiplier: 1.2,
    seasonal_factor: 1.0,
    growth_rate: 0.0,
    duration_days: 30,
  });
  const [demandResult, setDemandResult] = useState<DemandScenarioResponse | null>(null);

  // Disruption Scenario State
  const [disruptionParams, setDisruptionParams] = useState({
    scenario_name: 'Supply Delay',
    disruption_type: 'supplier_delay',
    severity: 0.5,
    duration_days: 14,
  });
  const [disruptionResult, setDisruptionResult] = useState<DisruptionScenarioResponse | null>(null);

  // Budget Optimization State
  const [budgetParams, setBudgetParams] = useState({
    total_budget: 100000,
    optimization_objective: 'maximize_service_level',
  });
  const [budgetResult, setBudgetResult] = useState<BudgetOptimizationResponse | null>(null);

  // Service Level Tradeoff State
  const [serviceLevelParams, setServiceLevelParams] = useState({
    min_service_level: 80,
    max_service_level: 99.5,
    steps: 10,
  });
  const [serviceLevelResult, setServiceLevelResult] = useState<ServiceLevelTradeoffResponse | null>(null);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError(null);
  };

  // Sensitivity Analysis Handler
  const runSensitivityAnalysis = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.analyzeSensitivity(sensitivityParams);
      setSensitivityResult(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error running sensitivity analysis');
    } finally {
      setLoading(false);
    }
  };

  // Demand Scenario Handler
  const runDemandScenario = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.modelDemandScenario(demandParams);
      setDemandResult(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error modeling demand scenario');
    } finally {
      setLoading(false);
    }
  };

  // Disruption Scenario Handler
  const runDisruptionScenario = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.simulateDisruption(disruptionParams);
      setDisruptionResult(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error simulating disruption');
    } finally {
      setLoading(false);
    }
  };

  // Budget Optimization Handler
  const runBudgetOptimization = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.optimizeBudget(budgetParams);
      setBudgetResult(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error optimizing budget');
    } finally {
      setLoading(false);
    }
  };

  // Service Level Tradeoff Handler
  const runServiceLevelTradeoff = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.analyzeServiceLevelTradeoff(serviceLevelParams);
      setServiceLevelResult(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error analyzing service level tradeoff');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography sx={{ fontSize: 'var(--text-lg)', fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)' }}>
        What-If Scenario Builder
      </Typography>
      <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', mt: 0.5, mb: 2 }}>
        Model different scenarios to understand their impact on inventory management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box>
        <Box sx={{ borderBottom: '1px solid var(--color-border)', mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
            <Tab label="Sensitivity Analysis" />
            <Tab label="Demand Scenarios" />
            <Tab label="Supply Disruption" />
            <Tab label="Budget Optimization" />
            <Tab label="Service Level Tradeoff" />
          </Tabs>
        </Box>

        {/* Sensitivity Analysis Tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Configuration
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    label="Parameter"
                    value={sensitivityParams.parameter}
                    onChange={(e) => setSensitivityParams({ ...sensitivityParams, parameter: e.target.value })}
                    margin="normal"
                  >
                    <MenuItem value="reorder_point">Reorder Point</MenuItem>
                    <MenuItem value="safety_stock">Safety Stock</MenuItem>
                    <MenuItem value="lead_time">Lead Time</MenuItem>
                  </TextField>
                  <TextField
                    fullWidth
                    type="number"
                    label="Base Value"
                    value={sensitivityParams.base_value}
                    onChange={(e) => setSensitivityParams({ ...sensitivityParams, base_value: parseFloat(e.target.value) })}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    type="number"
                    label="Variation Range (±%)"
                    value={sensitivityParams.variation_range * 100}
                    onChange={(e) => setSensitivityParams({ ...sensitivityParams, variation_range: parseFloat(e.target.value) / 100 })}
                    margin="normal"
                    inputProps={{ step: 10, min: 10, max: 100 }}
                  />
                  <TextField
                    fullWidth
                    type="number"
                    label="Analysis Steps"
                    value={sensitivityParams.steps}
                    onChange={(e) => setSensitivityParams({ ...sensitivityParams, steps: parseInt(e.target.value) })}
                    margin="normal"
                    inputProps={{ step: 1, min: 5, max: 20 }}
                  />
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} /> : <RunIcon />}
                    onClick={runSensitivityAnalysis}
                    disabled={loading}
                    sx={{ mt: 2 }}
                  >
                    Run Analysis
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={8}>
              {sensitivityResult && (
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Results
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={4}>
                        <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', p: '12px 16px', textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            Optimal Value
                          </Typography>
                          <Typography variant="h5">
                            {sensitivityResult.optimal_value.toFixed(2)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', p: '12px 16px', textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            Elasticity
                          </Typography>
                          <Typography variant="h5">
                            {sensitivityResult.elasticity.toFixed(3)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', p: '12px 16px', textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            Base Value
                          </Typography>
                          <Typography variant="h5">
                            {sensitivityResult.base_value.toFixed(2)}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={sensitivityResult.sensitivity_curve}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="parameter_value" label={{ value: sensitivityResult.parameter, position: 'insideBottom', offset: -5 }} />
                        <YAxis yAxisId="left" label={{ value: 'Cost', angle: -90, position: 'insideLeft' }} />
                        <YAxis yAxisId="right" orientation="right" label={{ value: 'Service Level %', angle: 90, position: 'insideRight' }} />
                        <Tooltip />
                        <Legend />
                        <Line yAxisId="left" type="monotone" dataKey="total_cost" stroke="#8B2438" name="Total Cost" />
                        <Line yAxisId="right" type="monotone" dataKey="service_level" stroke="#16A34A" name="Service Level" />
                        <Line yAxisId="left" type="monotone" dataKey="holding_cost" stroke="#C9A96E" name="Holding Cost" />
                      </LineChart>
                    </ResponsiveContainer>

                    {sensitivityResult.insights && sensitivityResult.insights.length > 0 && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" gutterBottom>
                          Insights
                        </Typography>
                        {sensitivityResult.insights.map((insight, index) => (
                          <Alert key={index} severity="info" sx={{ mb: 1 }}>
                            {insight}
                          </Alert>
                        ))}
                      </>
                    )}
                  </CardContent>
                </Card>
              )}
            </Grid>
          </Grid>
        </TabPanel>

        {/* Demand Scenario Tab */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Configuration
                  </Typography>
                  <TextField
                    fullWidth
                    label="Scenario Name"
                    value={demandParams.scenario_name}
                    onChange={(e) => setDemandParams({ ...demandParams, scenario_name: e.target.value })}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    type="number"
                    label="Demand Multiplier"
                    value={demandParams.demand_multiplier}
                    onChange={(e) => setDemandParams({ ...demandParams, demand_multiplier: parseFloat(e.target.value) })}
                    margin="normal"
                    inputProps={{ step: 0.1, min: 0.1, max: 5.0 }}
                  />
                  <TextField
                    fullWidth
                    type="number"
                    label="Seasonal Factor"
                    value={demandParams.seasonal_factor}
                    onChange={(e) => setDemandParams({ ...demandParams, seasonal_factor: parseFloat(e.target.value) })}
                    margin="normal"
                    inputProps={{ step: 0.1, min: 0.5, max: 2.0 }}
                  />
                  <TextField
                    fullWidth
                    type="number"
                    label="Growth Rate (%)"
                    value={demandParams.growth_rate * 100}
                    onChange={(e) => setDemandParams({ ...demandParams, growth_rate: parseFloat(e.target.value) / 100 })}
                    margin="normal"
                    inputProps={{ step: 1, min: -50, max: 100 }}
                  />
                  <TextField
                    fullWidth
                    type="number"
                    label="Duration (Days)"
                    value={demandParams.duration_days}
                    onChange={(e) => setDemandParams({ ...demandParams, duration_days: parseInt(e.target.value) })}
                    margin="normal"
                    inputProps={{ step: 1, min: 7, max: 90 }}
                  />
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} /> : <RunIcon />}
                    onClick={runDemandScenario}
                    disabled={loading}
                    sx={{ mt: 2 }}
                  >
                    Run Scenario
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={8}>
              {demandResult && (
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Results - {demandResult.scenario_name}
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={3}>
                        <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', p: '12px 16px', textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            Total Forecast
                          </Typography>
                          <Typography variant="h6">
                            {demandResult.total_forecasted_demand.toFixed(0)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={3}>
                        <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', p: '12px 16px', textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            Peak Day
                          </Typography>
                          <Typography variant="h6">
                            {demandResult.peak_demand_day}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={3}>
                        <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', p: '12px 16px', textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            Required Capacity
                          </Typography>
                          <Typography variant="h6">
                            {demandResult.required_inventory_capacity}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={3}>
                        <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', p: '12px 16px', textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            Stockout Risk
                          </Typography>
                          <Typography variant="h6">
                            {demandResult.stockout_risk_percentage.toFixed(1)}%
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={demandResult.daily_forecast.slice(0, 30)}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="day" label={{ value: 'Day', position: 'insideBottom', offset: -5 }} />
                        <YAxis label={{ value: 'Units', angle: -90, position: 'insideLeft' }} />
                        <Tooltip />
                        <Legend />
                        <Area type="monotone" dataKey="forecasted_demand" stroke="#8B2438" fill="#8B2438" name="Forecasted Demand" />
                        <Area type="monotone" dataKey="required_inventory" stroke="#16A34A" fill="#16A34A" name="Required Inventory" />
                      </AreaChart>
                    </ResponsiveContainer>

                    {demandResult.recommendations && demandResult.recommendations.length > 0 && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" gutterBottom>
                          Recommendations
                        </Typography>
                        {demandResult.recommendations.map((rec, index) => (
                          <Alert key={index} severity="success" sx={{ mb: 1 }}>
                            {rec}
                          </Alert>
                        ))}
                      </>
                    )}
                  </CardContent>
                </Card>
              )}
            </Grid>
          </Grid>
        </TabPanel>

        {/* Disruption Scenario Tab */}
        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Configuration
                  </Typography>
                  <TextField
                    fullWidth
                    label="Scenario Name"
                    value={disruptionParams.scenario_name}
                    onChange={(e) => setDisruptionParams({ ...disruptionParams, scenario_name: e.target.value })}
                    margin="normal"
                  />
                  <TextField
                    select
                    fullWidth
                    label="Disruption Type"
                    value={disruptionParams.disruption_type}
                    onChange={(e) => setDisruptionParams({ ...disruptionParams, disruption_type: e.target.value })}
                    margin="normal"
                  >
                    <MenuItem value="supplier_delay">Supplier Delay</MenuItem>
                    <MenuItem value="supplier_outage">Supplier Outage</MenuItem>
                    <MenuItem value="transportation_delay">Transportation Delay</MenuItem>
                    <MenuItem value="quality_issue">Quality Issue</MenuItem>
                    <MenuItem value="capacity_constraint">Capacity Constraint</MenuItem>
                  </TextField>
                  <TextField
                    fullWidth
                    type="number"
                    label="Severity (0-1)"
                    value={disruptionParams.severity}
                    onChange={(e) => setDisruptionParams({ ...disruptionParams, severity: parseFloat(e.target.value) })}
                    margin="normal"
                    inputProps={{ step: 0.1, min: 0.1, max: 1.0 }}
                  />
                  <TextField
                    fullWidth
                    type="number"
                    label="Duration (Days)"
                    value={disruptionParams.duration_days}
                    onChange={(e) => setDisruptionParams({ ...disruptionParams, duration_days: parseInt(e.target.value) })}
                    margin="normal"
                    inputProps={{ step: 1, min: 1, max: 90 }}
                  />
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} /> : <RunIcon />}
                    onClick={runDisruptionScenario}
                    disabled={loading}
                    sx={{ mt: 2 }}
                  >
                    Simulate Disruption
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={8}>
              {disruptionResult && (
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Impact Analysis - {disruptionResult.scenario_name}
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={3}>
                        <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', p: '12px 16px', textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            Revenue Loss
                          </Typography>
                          <Typography variant="h6">
                            {fmtCurrency(disruptionResult.estimated_revenue_loss)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={3}>
                        <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', p: '12px 16px', textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            Stockout Days
                          </Typography>
                          <Typography variant="h6">
                            {disruptionResult.estimated_stockout_days}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={3}>
                        <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', p: '12px 16px', textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            Affected Products
                          </Typography>
                          <Typography variant="h6">
                            {disruptionResult.affected_products_count}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={3}>
                        <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', p: '12px 16px', textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            Recovery Time
                          </Typography>
                          <Typography variant="h6">
                            {disruptionResult.recovery_time_days} days
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    {disruptionResult.mitigation_strategies && disruptionResult.mitigation_strategies.length > 0 && (
                      <>
                        <Typography variant="subtitle2" gutterBottom>
                          Mitigation Strategies
                        </Typography>
                        {disruptionResult.mitigation_strategies.map((strategy, index) => (
                          <Alert key={index} severity="warning" sx={{ mb: 1 }}>
                            {strategy}
                          </Alert>
                        ))}
                      </>
                    )}
                  </CardContent>
                </Card>
              )}
            </Grid>
          </Grid>
        </TabPanel>

        {/* Budget Optimization Tab */}
        <TabPanel value={tabValue} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Configuration
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    label={`Total Budget ${fmtCurrencyLabel()}`}
                    value={budgetParams.total_budget}
                    onChange={(e) => setBudgetParams({ ...budgetParams, total_budget: parseFloat(e.target.value) })}
                    margin="normal"
                    inputProps={{ step: 1000, min: 1000 }}
                  />
                  <TextField
                    select
                    fullWidth
                    label="Optimization Objective"
                    value={budgetParams.optimization_objective}
                    onChange={(e) => setBudgetParams({ ...budgetParams, optimization_objective: e.target.value })}
                    margin="normal"
                  >
                    <MenuItem value="maximize_service_level">Maximize Service Level</MenuItem>
                    <MenuItem value="minimize_stockout_risk">Minimize Stockout Risk</MenuItem>
                  </TextField>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} /> : <RunIcon />}
                    onClick={runBudgetOptimization}
                    disabled={loading}
                    sx={{ mt: 2 }}
                  >
                    Optimize Budget
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={8}>
              {budgetResult && (
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Optimization Results
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={3}>
                        <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', p: '12px 16px', textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            Budget Utilized
                          </Typography>
                          <Typography variant="h6">
                            {budgetResult.budget_utilization_percent.toFixed(1)}%
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={3}>
                        <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', p: '12px 16px', textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            Products
                          </Typography>
                          <Typography variant="h6">
                            {budgetResult.products_count}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={3}>
                        <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', p: '12px 16px', textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            Avg Service Level
                          </Typography>
                          <Typography variant="h6">
                            {budgetResult.avg_service_level.toFixed(1)}%
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={3}>
                        <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', p: '12px 16px', textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            Units Ordered
                          </Typography>
                          <Typography variant="h6">
                            {budgetResult.total_units_ordered}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Product</TableCell>
                            <TableCell>SKU</TableCell>
                            <TableCell>Item Status</TableCell>
                            <TableCell align="right">Budget</TableCell>
                            <TableCell align="right">Quantity</TableCell>
                            <TableCell align="right">Service Level</TableCell>
                            <TableCell align="right">Priority</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {budgetResult.allocations.slice(0, 10).map((allocation) => (
                            <TableRow key={allocation.product_id}>
                              <TableCell>{allocation.product_name}</TableCell>
                              <TableCell>{allocation.sku}</TableCell>
                              <TableCell sx={{ py: '6px', px: '8px' }}>
                                {(allocation as any).lifecycle_stage ? (
                                  <Chip
                                    label={(allocation as any).lifecycle_stage}
                                    size="small"
                                    color={
                                      (allocation as any).lifecycle_stage === 'NEW' ? 'info' :
                                      (allocation as any).lifecycle_stage === 'GROWTH' ? 'success' :
                                      (allocation as any).lifecycle_stage === 'DECLINING' ? 'warning' :
                                      ((allocation as any).lifecycle_stage === 'PHASE_OUT' || (allocation as any).lifecycle_stage === 'DISCONTINUED') ? 'error' :
                                      'default'
                                    }
                                    variant="outlined"
                                    sx={{ fontSize: '10px', height: 20 }}
                                  />
                                ) : (
                                  <Typography sx={{ fontSize: '10px', color: 'var(--color-text-muted)' }}>—</Typography>
                                )}
                              </TableCell>
                              <TableCell align="right">{fmtCurrency(allocation.allocated_budget)}</TableCell>
                              <TableCell align="right">{allocation.order_quantity}</TableCell>
                              <TableCell align="right">{allocation.expected_service_level.toFixed(1)}%</TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={allocation.priority_score.toFixed(2)}
                                  size="small"
                                  color={allocation.priority_score > 0.7 ? 'error' : allocation.priority_score > 0.4 ? 'warning' : 'success'}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {budgetResult.recommendations && budgetResult.recommendations.length > 0 && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" gutterBottom>
                          Recommendations
                        </Typography>
                        {budgetResult.recommendations.map((rec, index) => (
                          <Alert key={index} severity="info" sx={{ mb: 1 }}>
                            {rec}
                          </Alert>
                        ))}
                      </>
                    )}
                  </CardContent>
                </Card>
              )}
            </Grid>
          </Grid>
        </TabPanel>

        {/* Service Level Tradeoff Tab */}
        <TabPanel value={tabValue} index={4}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Configuration
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    label="Min Service Level (%)"
                    value={serviceLevelParams.min_service_level}
                    onChange={(e) => setServiceLevelParams({ ...serviceLevelParams, min_service_level: parseFloat(e.target.value) })}
                    margin="normal"
                    inputProps={{ step: 5, min: 50, max: 95 }}
                  />
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Service Level (%)"
                    value={serviceLevelParams.max_service_level}
                    onChange={(e) => setServiceLevelParams({ ...serviceLevelParams, max_service_level: parseFloat(e.target.value) })}
                    margin="normal"
                    inputProps={{ step: 0.5, min: 90, max: 99.9 }}
                  />
                  <TextField
                    fullWidth
                    type="number"
                    label="Analysis Steps"
                    value={serviceLevelParams.steps}
                    onChange={(e) => setServiceLevelParams({ ...serviceLevelParams, steps: parseInt(e.target.value) })}
                    margin="normal"
                    inputProps={{ step: 1, min: 5, max: 20 }}
                  />
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} /> : <RunIcon />}
                    onClick={runServiceLevelTradeoff}
                    disabled={loading}
                    sx={{ mt: 2 }}
                  >
                    Analyze Tradeoff
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={8}>
              {serviceLevelResult && (
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Service Level vs Cost Analysis
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={4}>
                        <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', p: '12px 16px', textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            Optimal Service Level
                          </Typography>
                          <Typography variant="h6">
                            {serviceLevelResult.optimal_service_level.toFixed(1)}%
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', p: '12px 16px', textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            Optimal Cost
                          </Typography>
                          <Typography variant="h6">
                            {fmtCurrency(serviceLevelResult.optimal_cost)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', p: '12px 16px', textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            Cost per Point
                          </Typography>
                          <Typography variant="h6">
                            {fmtCurrency(serviceLevelResult.cost_per_service_point)}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={serviceLevelResult.tradeoff_curve}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="service_level_percent" label={{ value: 'Service Level %', position: 'insideBottom', offset: -5 }} />
                        <YAxis label={{ value: `Cost ${fmtCurrencyLabel()}`, angle: -90, position: 'insideLeft' }} />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="total_cost" stroke="#8B2438" name="Total Cost" strokeWidth={2} />
                        <Line type="monotone" dataKey="inventory_cost" stroke="#16A34A" name="Inventory Cost" />
                        <Line type="monotone" dataKey="stockout_cost" stroke="#C9A96E" name="Stockout Cost" />
                      </LineChart>
                    </ResponsiveContainer>

                    {serviceLevelResult.recommendations && serviceLevelResult.recommendations.length > 0 && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" gutterBottom>
                          Recommendations
                        </Typography>
                        {serviceLevelResult.recommendations.map((rec, index) => (
                          <Alert key={index} severity="info" sx={{ mb: 1 }}>
                            {rec}
                          </Alert>
                        ))}
                      </>
                    )}
                  </CardContent>
                </Card>
              )}
            </Grid>
          </Grid>
        </TabPanel>
      </Box>
    </Box>
  );
};

export default ScenarioBuilder;
